'use client';

import {
  SimpleFooter,
  type SimpleFooterProps,
} from '@jouzen/ecom-components/layout';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { PropsWithChildren } from 'react';

import usePageView from '@/analytics/hooks/usePageView';
import type { ThemeConfig } from '@/types';

import useLegalLinks from './useLegalLinks';

const MAIN_CONTENT_ID = 'main-content';

export interface PageLayoutProps {
  readonly className?: string;
  readonly hideSocialLinks?: SimpleFooterProps['hideSocialLinks'];
  readonly name?: string;
  readonly theme?: ThemeConfig;
  readonly padding?: 'top' | 'bottom' | 'both' | 'none';
  readonly pageName: string;
}

const PageLayout = ({
  children,
  className,
  hideSocialLinks,
  pageName,
  name = 'Enrollment Order Portal',
  theme = {},
  padding = 'bottom',
}: PropsWithChildren<PageLayoutProps>): JSX.Element => {
  const t = useTranslations();
  const legalLinks = useLegalLinks();

  usePageView(pageName);

  return (
    <>
      <div className="sr-only focus-within:not-sr-only focus-within:px-4 focus-within:py-2">
        <a data-cy="skip-to-content" href={`#${MAIN_CONTENT_ID}`}>
          {t('header_skip_to_content')}
        </a>
      </div>
      <div
        className={cx(
          theme.bgColorClass,
          className,
          'flex min-h-screen flex-col',
        )}
        data-cy="page-wrapper"
        id="page-wrapper"
      >
        <div className="pointer-events-none relative grow">
          <main
            aria-label={t('main_content')}
            className="pointer-events-auto"
            data-cy={MAIN_CONTENT_ID}
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
          >
            <div
              className={cx({
                'pt-36': padding === 'top',
                'pb-16': padding === 'bottom',
                'pb-16 pt-36': padding === 'both',
              })}
              data-cy={`page-${name}`}
            >
              {/* Typography wrapper sets default text color */}
              <Typography
                Element="div"
                color={theme.typographyColor}
                data-testid="page-layout"
                className="grow"
              >
                {children}
              </Typography>
            </div>
          </main>
        </div>
        <SimpleFooter
          hideSocialLinks={hideSocialLinks}
          legalLinks={legalLinks}
        />
      </div>
    </>
  );
};

export default PageLayout;
